/**
 * ScreenB — Numbers + Events public screen.
 *
 * Layout: 2/5 left (Key World Stats) + 3/5 right (Main Global Events)
 * Data: auto-calculated from DB (countries, deployments, snapshots)
 * Events: from screen_b_events table (moderator-curated)
 *
 * SPEC: MODULES/M8_PUBLIC_SCREEN/SPEC_PUBLIC_SCREEN_SPRINT.md
 */

import { useEffect, useState, useCallback, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { CountryEmblem } from '@/components/CountryEmblem'

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */

interface CountryRow {
  id: string
  sim_name: string
  color_ui: string | null
  gdp: number
  stability: number
  nuclear_level: number
  nuclear_confirmed: boolean
  ai_level: number
  mil_ground: number
  mil_naval: number
  mil_tactical_air: number
  mil_strategic_missiles: number
  mil_air_defense: number
}

interface Snapshot {
  country_code: string
  gdp: number
  stability: number
  mil_ground: number
  mil_naval: number
  mil_tactical_air: number
  mil_strategic_missiles: number
  mil_air_defense: number
  nuclear_level: number
  ai_level: number
}

interface DeploymentCounts {
  [countryCode: string]: {
    ground: number; naval: number; tactical_air: number
    air_defense: number; strategic_missile: number
  }
}

interface ScreenBEvent {
  id: string
  quadrant: string
  headline: string
  description: string
  sort_order: number
}

interface RankedCountry {
  id: string
  name: string
  color: string
  gdp: number
  gdpDelta: number | null
  stability: number
  stabilityDelta: number | null
  milIndex: number
  milDelta: number | null
  nuclearScore: number
  aiLevel: number
  aiProgress: number
}

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

function calcConventional(g: number, n: number, a: number, ad: number, m: number): number {
  return g * 1 + n * 3 + a * 2 + ad * 3 + m * 2
}

function calcNuclearScore(missiles: number, nucLevel: number): number {
  return missiles * 2 * nucLevel
}

const QUADRANTS = ['military', 'diplomacy', 'political', 'economy_tech'] as const
const QUADRANT_LABELS: Record<string, string> = {
  military: 'Military',
  diplomacy: 'Diplomacy',
  political: 'Political',
  economy_tech: 'Economy & Tech',
}
const QUADRANT_COLORS: Record<string, string> = {
  military: 'rgba(248,113,113,0.6)',
  diplomacy: 'rgba(147,197,253,0.6)',
  political: 'rgba(110,231,183,0.6)',
  economy_tech: 'rgba(252,211,77,0.6)',
}

const TOP_N = 8
const ROTATE_SIZE = 3
const ROTATE_INTERVAL = 5000

/* -------------------------------------------------------------------------- */
/*  Delta display                                                             */
/* -------------------------------------------------------------------------- */

function Delta({ value, suffix = '' }: { value: number | null; suffix?: string }) {
  if (value == null || Math.abs(value) < 0.01) return <span className="inline-block w-[32px] text-center text-white/10 text-[9px]">─</span>
  const isUp = value > 0
  const display = Math.abs(value) < 10 && Math.abs(value) !== Math.floor(Math.abs(value))
    ? Math.abs(value).toFixed(1) : Math.round(Math.abs(value)).toString()
  return (
    <span className={`inline-block w-[32px] text-center text-[9px] font-semibold ${isUp ? 'text-green-400' : 'text-red-400'}`}>
      {isUp ? '▲' : '▼'}{display}{suffix}
    </span>
  )
}

/* -------------------------------------------------------------------------- */
/*  Main Component                                                            */
/* -------------------------------------------------------------------------- */

export function ScreenB({ simId, currentRound, frozen, layoutMode = 'full' }: {
  simId: string
  currentRound: number
  frozen: boolean
  /** Layout zoom mode for the big-screen setup. 'full' shows both panels,
   *  'stats' shows only the country rankings, 'events' shows only the
   *  2x2 events grid. Set from PublicScreen header toggle.
   */
  layoutMode?: 'full' | 'stats' | 'events'
}) {
  const [countries, setCountries] = useState<RankedCountry[]>([])
  const [events, setEvents] = useState<Record<string, ScreenBEvent[]>>({})
  const [oilPrice, setOilPrice] = useState<number | null>(null)
  const [oilDelta, setOilDelta] = useState<number | null>(null)
  const [wars, setWars] = useState<{ pairs: string[]; count: number }>({ pairs: [], count: 0 })
  const [nuclearTop6, setNuclearTop6] = useState<{ id: string; name: string; color: string }[]>([])
  const [aiTop6, setAiTop6] = useState<{ id: string; name: string; color: string }[]>([])
  const [rotatePage, setRotatePage] = useState(0)

  /* ── Data fetching ─────────────────────────────────────────────────── */
  const loadData = useCallback(async () => {
    // Parallel fetch: countries, deployments, snapshots, events, wars, world_indicators
    const [countriesRes, deploymentsRes, snapshotsRes, eventsRes, warsRes, indicatorsRes] = await Promise.allSettled([
      supabase.from('countries')
        .select('id,sim_name,color_ui,gdp,stability,nuclear_level,nuclear_confirmed,ai_level,ai_rd_progress')
        .eq('sim_run_id', simId),
      supabase.from('deployments')
        .select('country_code,unit_type')
        .eq('sim_run_id', simId)
        .in('unit_status', ['active', 'reserve', 'embarked']),
      supabase.from('country_states_per_round')
        .select('country_code,gdp,stability,mil_ground,mil_naval,mil_tactical_air,mil_strategic_missiles,mil_air_defense,nuclear_level,ai_level')
        .eq('sim_run_id', simId)
        .eq('round_num', Math.max(0, currentRound - 1)),
      supabase.from('screen_b_events')
        .select('id,quadrant,headline,description,sort_order')
        .eq('sim_run_id', simId)
        .eq('round_num', currentRound)
        .eq('version', 'live')
        .order('sort_order'),
      supabase.from('relationships')
        .select('from_country_code,to_country_code')
        .eq('sim_run_id', simId)
        .eq('relationship', 'at_war'),
      // Oil price from world_state — initialised at sim creation, updated by
      // economic engine after each Phase B. Pulling from phase_b_complete
      // events meant the price showed '—' in R0 / pre-Phase-B states (the
      // event doesn't exist yet); world_state always has a current row.
      supabase.from('world_state')
        .select('round_num, oil_price')
        .eq('sim_run_id', simId)
        .order('round_num', { ascending: false })
        .limit(2),
    ])

    // Parse countries
    const rawCountries: CountryRow[] = countriesRes.status === 'fulfilled' ? (countriesRes.value.data ?? []) : []

    // Parse deployment counts
    const depCounts: DeploymentCounts = {}
    if (deploymentsRes.status === 'fulfilled' && deploymentsRes.value.data) {
      for (const d of deploymentsRes.value.data) {
        const cc = d.country_code as string
        const ut = d.unit_type as string
        if (!depCounts[cc]) depCounts[cc] = { ground: 0, naval: 0, tactical_air: 0, air_defense: 0, strategic_missile: 0 }
        depCounts[cc][ut as keyof typeof depCounts[string]] = (depCounts[cc][ut as keyof typeof depCounts[string]] || 0) + 1
      }
    }

    // Parse snapshots
    const snapshots: Snapshot[] = snapshotsRes.status === 'fulfilled' ? (snapshotsRes.value.data ?? []) : []
    const snapMap = new Map(snapshots.map(s => [s.country_code, s]))

    // Build ranked countries
    const ranked: RankedCountry[] = rawCountries
      .filter(c => c.id !== '_note')
      .map(c => {
        const dep = depCounts[c.id] || { ground: 0, naval: 0, tactical_air: 0, air_defense: 0, strategic_missile: 0 }
        const milIndex = calcConventional(dep.ground, dep.naval, dep.tactical_air, dep.air_defense, dep.strategic_missile)
        const nucScore = calcNuclearScore(dep.strategic_missile, c.nuclear_level)

        const snap = snapMap.get(c.id)
        const prevMil = snap ? calcConventional(snap.mil_ground, snap.mil_naval, snap.mil_tactical_air, snap.mil_air_defense, snap.mil_strategic_missiles) : null

        const gdp = Number(c.gdp) || 0
        const stab = Number(c.stability) || 0
        const prevGdp = snap ? Number(snap.gdp) || 0 : null
        const prevStab = snap ? Number(snap.stability) || 0 : null

        return {
          id: c.id,
          name: c.sim_name || c.id,
          color: c.color_ui || '#4A5568',
          gdp,
          gdpDelta: prevGdp != null && prevGdp > 0 ? ((gdp - prevGdp) / prevGdp) * 100 : null,
          stability: stab,
          stabilityDelta: prevStab != null ? stab - prevStab : null,
          milIndex,
          milDelta: prevMil != null ? milIndex - prevMil : null,
          nuclearScore: nucScore,
          aiLevel: c.ai_level || 0,
          aiProgress: Number(c.ai_rd_progress) || 0,
        }
      })
      .sort((a, b) => b.gdp - a.gdp)

    setCountries(ranked)

    // Nuclear top 6 (by nuclear score, only countries with nukes)
    const nucRanked = [...ranked].filter(c => c.nuclearScore > 0).sort((a, b) => b.nuclearScore - a.nuclearScore).slice(0, 6)
    setNuclearTop6(nucRanked.map(c => ({ id: c.id, name: c.name, color: c.color })))

    // AI top 6 (by ai_level, only countries with ai > 0)
    const aiRanked = [...ranked].filter(c => c.aiLevel > 0).sort((a, b) => b.aiLevel - a.aiLevel || b.aiProgress - a.aiProgress).slice(0, 6)
    setAiTop6(aiRanked.map(c => ({ id: c.id, name: c.name, color: c.color })))

    // Events by quadrant
    if (eventsRes.status === 'fulfilled' && eventsRes.value.data) {
      const grouped: Record<string, ScreenBEvent[]> = {}
      for (const q of QUADRANTS) grouped[q] = []
      for (const e of eventsRes.value.data) {
        const evt = e as ScreenBEvent
        if (grouped[evt.quadrant]) grouped[evt.quadrant].push(evt)
      }
      setEvents(grouped)
    }

    // Wars
    if (warsRes.status === 'fulfilled' && warsRes.value.data) {
      const seen = new Set<string>()
      const pairs: string[] = []
      for (const w of warsRes.value.data) {
        const a = (w.from_country_code as string)
        const b = (w.to_country_code as string)
        const key = [a, b].sort().join('–')
        if (!seen.has(key)) { seen.add(key); pairs.push(key) }
      }
      setWars({ pairs, count: pairs.length })
    }

    // Oil price from world_state — direct oil_price column on the row
    if (indicatorsRes.status === 'fulfilled' && indicatorsRes.value.data?.length) {
      const latestRow = indicatorsRes.value.data[0] as Record<string, unknown>
      const prevRow = indicatorsRes.value.data[1] as Record<string, unknown> | undefined
      const latest = latestRow?.oil_price != null ? Number(latestRow.oil_price) : null
      const prev = prevRow?.oil_price != null ? Number(prevRow.oil_price) : null
      setOilPrice(latest)
      setOilDelta(latest != null && prev != null ? latest - prev : null)
    }
  }, [simId, currentRound])

  /* ── Realtime + polling ────────────────────────────────────────────── */
  useEffect(() => {
    loadData()

    const ch = supabase.channel(`screen-b-${simId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'screen_b_events', filter: `sim_run_id=eq.${simId}` }, () => loadData())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'countries', filter: `sim_run_id=eq.${simId}` }, () => loadData())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'relationships', filter: `sim_run_id=eq.${simId}` }, () => loadData())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'world_state', filter: `sim_run_id=eq.${simId}` }, () => loadData())
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'observatory_events', filter: `sim_run_id=eq.${simId}` }, () => loadData())
      .subscribe()

    const poll = setInterval(loadData, 30000)

    return () => { supabase.removeChannel(ch); clearInterval(poll) }
  }, [simId, currentRound, loadData])

  /* ── Rotation timer ────────────────────────────────────────────────── */
  useEffect(() => {
    const interval = setInterval(() => setRotatePage(p => p + 1), ROTATE_INTERVAL)
    return () => clearInterval(interval)
  }, [])

  /* ── Derived data ──────────────────────────────────────────────────── */
  // In 'stats' layout the panel is zoomed in (see grid container below), and
  // only 1 rotating row fits in the visible window — overflow-hidden clips the
  // other 2 of ROTATE_SIZE=3. The rotation then appears to "skip by 3" because
  // we render 3 but only see 1. Use a smaller rotation slice in stats mode so
  // visible count and step match — countries cycle one at a time, 9, 10, 11...
  const rotateSize = layoutMode === 'stats' ? 1 : ROTATE_SIZE
  const topCountries = countries.slice(0, TOP_N)
  const restCountries = countries.slice(TOP_N)
  const rotateStart = restCountries.length > 0 ? (rotatePage * rotateSize) % restCountries.length : 0
  const rotatingSlice = restCountries.length > 0
    ? Array.from({ length: Math.min(rotateSize, restCountries.length) }, (_, i) =>
        restCountries[(rotateStart + i) % restCountries.length])
    : []

  /* ── Render ────────────────────────────────────────────────────────── */
  return (
    <div className="flex flex-col h-full">
      {/* Frozen overlay */}
      {frozen && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <span className="font-heading text-h2 text-white/50 tracking-widest uppercase">Round Processing...</span>
        </div>
      )}

      {/* Main: 2/5 + 3/5 in 'full'; single panel fills full width in 'stats' or 'events'.
          In single-panel modes we apply CSS `zoom` so the fixed-pixel typography
          inside the panel scales up to use the extra width — without `zoom` the
          panel just sits in the middle of a sea of empty space at the original
          density. `zoom` (unlike transform: scale) scales the layout dimensions
          too, so the panel still fills the row correctly. */}
      <div className="grid flex-1 min-h-0" style={{
        gridTemplateColumns: layoutMode === 'full' ? '2fr 3fr' : '1fr',
        gap: '16px',
        padding: '10px 24px 6px',
        zoom: layoutMode === 'full' ? 1 : 1.6,
      }}>

        {/* ═══ LEFT: Key World Stats ═══ (hidden when layoutMode === 'events') */}
        {layoutMode !== 'events' && (
        <div className="flex flex-col gap-2.5 min-h-0">
          {/* Main table */}
          <div className="flex-1 min-h-0 bg-white/[0.02] border border-white/[0.05] rounded-md flex flex-col overflow-hidden">
            <div className="px-3.5 py-1.5 border-b border-white/[0.05] flex items-baseline gap-2.5">
              <span className="font-heading text-[13px] font-bold tracking-wider uppercase text-white/50">Key World Stats</span>
              <span className="font-data text-[9px] text-white/35">by GDP · round {currentRound}</span>
            </div>
            <div className="flex-1 overflow-hidden">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    {['#', 'Country', 'GDP ç', 'Stab', 'Mil Index'].map((h, i) => (
                      <th key={h} className={`font-data text-[9px] font-medium uppercase tracking-wide text-white/45 py-1 px-1.5 border-b border-white/[0.04] ${i < 2 ? 'text-left' : 'text-right'} ${i === 0 ? 'w-5 text-center' : ''}`}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {topCountries.map((c, i) => (
                    <tr key={c.id} className={i === 0 ? 'bg-[rgba(26,58,126,0.06)]' : i === 1 ? 'bg-[rgba(180,40,40,0.06)]' : ''}>
                      <td className="text-center font-data text-[11px] font-semibold text-white/35 py-[7px] px-1.5 border-b border-white/[0.025]">{i + 1}</td>
                      <td className="py-[7px] px-1.5 border-b border-white/[0.025]">
                        <div className="flex items-center gap-1.5">
                          <CountryEmblem countryId={c.id} color={c.color} size={18} />
                          <span className="font-body text-[13px] font-bold tracking-wide text-white/80">{c.name}</span>
                        </div>
                      </td>
                      <td className="font-data text-[14px] font-medium text-white/75 py-[7px] px-1.5 border-b border-white/[0.025]">
                        <div className="flex items-center justify-end"><span>{Math.round(c.gdp)}</span><Delta value={c.gdpDelta} suffix="%" /></div>
                      </td>
                      <td className="font-data text-[14px] font-medium text-white/75 py-[7px] px-1.5 border-b border-white/[0.025]">
                        <div className="flex items-center justify-end"><span>{c.stability.toFixed(1)}</span><Delta value={c.stabilityDelta} /></div>
                      </td>
                      <td className="font-data text-[14px] font-medium text-white/75 py-[7px] px-1.5 border-b border-white/[0.025]">
                        <div className="flex items-center justify-end"><span>{c.milIndex}</span><Delta value={c.milDelta} /></div>
                      </td>
                    </tr>
                  ))}
                  {/* Separator */}
                  <tr><td colSpan={5} className="text-center text-[7px] text-white/10 tracking-widest py-0.5">· · ·</td></tr>
                  {/* Rotating rows */}
                  {rotatingSlice.map((c, i) => (
                    <tr key={`r-${c.id}`} className="animate-[fadeIn_0.4s_ease] opacity-50">
                      <td className="text-center font-data text-[11px] font-semibold text-white/35 py-[7px] px-1.5 border-b border-white/[0.025]">{TOP_N + rotateStart + i + 1}</td>
                      <td className="py-[7px] px-1.5 border-b border-white/[0.025]">
                        <div className="flex items-center gap-1.5">
                          <CountryEmblem countryId={c.id} color={c.color} size={18} />
                          <span className="font-body text-[13px] font-bold tracking-wide text-white/80">{c.name}</span>
                        </div>
                      </td>
                      <td className="font-data text-[14px] font-medium text-white/75 py-[7px] px-1.5 border-b border-white/[0.025]">
                        <div className="flex items-center justify-end"><span>{Math.round(c.gdp)}</span><Delta value={c.gdpDelta} suffix="%" /></div>
                      </td>
                      <td className="font-data text-[14px] font-medium text-white/75 py-[7px] px-1.5 border-b border-white/[0.025]">
                        <div className="flex items-center justify-end"><span>{c.stability.toFixed(1)}</span><Delta value={c.stabilityDelta} /></div>
                      </td>
                      <td className="font-data text-[14px] font-medium text-white/75 py-[7px] px-1.5 border-b border-white/[0.025]">
                        <div className="flex items-center justify-end"><span>{c.milIndex}</span><Delta value={c.milDelta} /></div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mini rank strips: Nuclear Might | AI Tech */}
          <div className="grid grid-cols-2 gap-2 flex-shrink-0">
            <MiniRankCard title="Nuclear Might" items={nuclearTop6} />
            <MiniRankCard title="AI Tech" items={aiTop6} />
          </div>
        </div>
        )}

        {/* ═══ RIGHT: Main Global Events ═══ (hidden when layoutMode === 'stats') */}
        {layoutMode !== 'stats' && (
        <div className="flex flex-col min-h-0 gap-2">
          <div className="flex items-baseline gap-2.5 px-2.5 pt-1">
            <span className="font-heading text-[13px] font-bold tracking-wider uppercase text-white/50">Main Global Events</span>
            <span className="font-data text-[9px] text-white/35">round {currentRound}</span>
          </div>
          <div className="grid grid-cols-2 grid-rows-2 gap-2 flex-1 min-h-0">
            {QUADRANTS.map(q => (
              <EventQuadrant key={q} quadrant={q} events={q === 'economy_tech' ? [] : (events[q] || [])} />
            ))}
          </div>
        </div>
        )}
      </div>

      {/* ── Footer strip ─────────────────────────────────────────────── */}
      <div className="flex justify-center items-center gap-5 py-1 border-t border-white/[0.04] flex-shrink-0 font-data text-[11px] text-white/35">
        <span>OIL <span className="text-white/60 font-semibold">ç{oilPrice ?? '—'}</span>/bbl {oilDelta != null && <Delta value={oilDelta} suffix="" />}</span>
        <span>|</span>
        <span>WARS <span className="text-red-400/60 font-semibold">{wars.count} active</span></span>
        <span className="text-white/20">{wars.pairs.join(' · ')}</span>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  Sub-components                                                            */
/* -------------------------------------------------------------------------- */

function MiniRankCard({ title, items }: { title: string; items: { id: string; name: string; color: string }[] }) {
  return (
    <div className="bg-white/[0.02] border border-white/[0.05] rounded-md px-2 py-1.5">
      <div className="font-data text-[8px] font-semibold uppercase tracking-wide text-white/45 mb-1">{title}</div>
      {items.map((c, i) => (
        <div key={c.id} className="flex items-center gap-1 py-[2px] font-data text-[11px] text-white/55">
          <span className="text-white/35 text-[9px] w-3.5 text-right">{i + 1}</span>
          <CountryEmblem countryId={c.id} color={c.color} size={13} />
          <span className="font-body text-[11px] font-semibold">{c.name}</span>
        </div>
      ))}
    </div>
  )
}

function EventQuadrant({ quadrant, events }: { quadrant: string; events: ScreenBEvent[] }) {
  return (
    <div className="bg-white/[0.02] border border-white/[0.05] rounded-md flex flex-col overflow-hidden min-h-0">
      <div className="px-2.5 py-1.5 border-b border-white/[0.04]"
           style={{ color: QUADRANT_COLORS[quadrant] }}>
        <span className="font-heading text-[13px] font-bold">{QUADRANT_LABELS[quadrant]}</span>
      </div>
      <div className="flex-1 px-2.5 py-1 overflow-y-auto">
        {events.length === 0 && (
          <div className="text-white/15 font-body text-[11px] italic py-2">No events yet</div>
        )}
        {events.map(e => (
          <div key={e.id} className="py-1.5 border-b border-white/[0.02] last:border-0">
            <div className="font-body text-[12px] leading-snug text-white/60">
              <strong className="text-white/80 font-semibold">{e.headline}</strong>
              {e.description && <> — {e.description}</>}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
